import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getJwtToken } from '../utils/jwtService';
import promptService from '../services/promptService';
import Button from './ui/Button';
import Input from './ui/Input';
import { ArrowLeft, Save, AlertCircle, CheckCircle, Loader2, History, Copy, RotateCcw } from 'lucide-react';
import './PromptSettings.css';

// Translation-engine prompts (the original system prompts).
const TRANSLATION_VERSIONS = [
    { value: 'default', label: 'Default (Legacy)' },
    { value: 'v1', label: 'V1 - Standard Translation' },
    { value: 'v2', label: 'V2 - Interpretive Translation' }
];

// Meeting AI instruction prompts. Values are the engineVersions the backend reads
// (MeetingAiPromptProvider.EngineVersion) — editing these here drives summary/Q&A/suggested at runtime.
const MEETING_AI_VERSIONS = [
    { value: 'meeting-ai-summary', label: 'Summary' },
    { value: 'meeting-ai-qa', label: 'Q&A (Ask Anything)' },
    { value: 'meeting-ai-suggested', label: 'Suggested Questions' }
];

const PROMPT_FAMILIES = {
    translation: { label: 'Translation Engine', title: 'Translator Engine Prompt', versions: TRANSLATION_VERSIONS },
    meetingAi: { label: 'Meeting AI', title: 'Meeting AI Prompts', versions: MEETING_AI_VERSIONS }
};

// Per-Meeting-AI-prompt editor hints: available placeholders + output contract.
const MEETING_AI_HELP = {
    'meeting-ai-summary': 'Placeholders: {languageName}, {targetLanguage}. The transcript is appended automatically after this instruction. Leave unconfigured to use the built-in default.',
    'meeting-ai-qa': 'The transcript and prior Q&A turns are supplied automatically. The model should answer in the language of the user’s most recent question. Leave unconfigured to use the built-in default.',
    'meeting-ai-suggested': 'Placeholders: {languageName}, {targetLanguage}. MUST output ONLY a JSON array of 8–10 short question strings (no prose). Leave unconfigured to use the built-in default.'
};

const isMeetingAiVersion = (value) => MEETING_AI_VERSIONS.some(v => v.value === value);

const PromptSettings = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isCopying, setIsCopying] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    // Prompt family ('translation' | 'meetingAi') + engine version within that family.
    const [promptFamily, setPromptFamily] = useState('translation');
    const [selectedEngineVersion, setSelectedEngineVersion] = useState('default');
    const isMeetingAi = promptFamily === 'meetingAi';

    // Built-in Meeting AI defaults (from GET /meeting-ai/prompts/defaults). Kept in a ref (no state) so
    // the load-prompt effect can seed an unconfigured prompt without re-rendering on defaults arrival.
    // The "Reset to Default" button stays enabled and fetches on demand, so nothing renders off this.
    const meetingAiDefaultsRef = useRef({});
    const defaultsPromiseRef = useRef(null);

    // Fetch the Meeting AI defaults at most once (memoised), on demand. Robust to the JWT hydrating
    // after mount and to the defaults fetch racing the prompt load — callers await this before seeding.
    const ensureMeetingAiDefaults = useCallback(async () => {
        if (Object.keys(meetingAiDefaultsRef.current).length > 0) {
            return meetingAiDefaultsRef.current;
        }
        if (!defaultsPromiseRef.current) {
            defaultsPromiseRef.current = promptService.getMeetingAiPromptDefaults()
                .then(d => {
                    const map = {
                        'meeting-ai-summary': d.summary || d.Summary || '',
                        'meeting-ai-qa': d.qa || d.Qa || '',
                        'meeting-ai-suggested': d.suggested || d.Suggested || ''
                    };
                    meetingAiDefaultsRef.current = map;
                    return map;
                })
                .catch(error => {
                    // Non-fatal: editor still works, just without pre-fill/reset. Allow a later retry.
                    console.error('Failed to load Meeting AI prompt defaults:', error);
                    defaultsPromiseRef.current = null;
                    return {};
                });
        }
        return defaultsPromiseRef.current;
    }, []);

    const [promptData, setPromptData] = useState({
        systemPromptTemplate: '',
        description: '',
        changeNotes: '',
        version: 0,
        updatedAt: null,
        lastModifiedBy: '',
        engineVersion: 'default'
    });

    const [historyItems, setHistoryItems] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [selectedHistoryVersion, setSelectedHistoryVersion] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [copyTargetVersion, setCopyTargetVersion] = useState('v2');
    const [historyError, setHistoryError] = useState(null);

    // Redirect if not a Platform Admin (SuperAdmin, IsPlatformAdmin, or Admin)
    useEffect(() => {
        const hasPlatformAccess = user?.IsPlatformAdmin || user?.Role === 'SuperAdmin' || user?.Role === 'Admin';
        if (user && !hasPlatformAccess) {
            navigate('/host');
        }
    }, [user, navigate]);

    // Prefetch the Meeting AI defaults when the user enters the Meeting AI family (not on mount, so
    // an async-hydrating JWT is ready by then), enabling pre-fill and "Reset to Default".
    useEffect(() => {
        if (isMeetingAi) {
            ensureMeetingAiDefaults();
        }
    }, [isMeetingAi, ensureMeetingAiDefaults]);

    // Load prompt when engine version changes
    useEffect(() => {
        const loadPrompt = async () => {
            if (!getJwtToken()) return;

            setIsLoading(true);
            try {
                const data = await promptService.getPrompt(selectedEngineVersion);
                console.log('Fetched prompt data for engine', selectedEngineVersion, ':', data);

                setPromptData({
                    systemPromptTemplate: data.systemPromptTemplate || data.SystemPromptTemplate || '',
                    description: data.description || data.Description || '',
                    changeNotes: '', // Always clear change notes on load
                    version: data.version || data.Version || 0,
                    updatedAt: data.updatedAt || data.UpdatedAt || null,
                    engineVersion: data.engineVersion || data.EngineVersion || selectedEngineVersion
                });
            } catch (error) {
                console.error('Failed to load prompt:', error);
                // If prompt doesn't exist yet, that's fine - we'll create it on save
                if (error.message.includes('has not been configured yet') || error.message.includes('404')) {
                    console.log('Prompt not found for engine', selectedEngineVersion, ', initializing as new');
                    // For Meeting AI prompts, seed the editor with the built-in default so admins edit
                    // FROM the real instruction (with its placeholders / JSON output contract) rather than
                    // a blank box. Await the (memoised) defaults so seeding never loses a race with the
                    // defaults fetch. Translation prompts stay blank (their legacy behaviour).
                    let seed = '';
                    if (isMeetingAiVersion(selectedEngineVersion)) {
                        const defaults = await ensureMeetingAiDefaults();
                        seed = defaults[selectedEngineVersion] || '';
                    }
                    setPromptData(prev => ({
                        ...prev,
                        version: 0,
                        updatedAt: null,
                        systemPromptTemplate: seed,
                        description: '',
                        changeNotes: '',
                        engineVersion: selectedEngineVersion
                    }));
                } else {
                    setStatus({ type: 'error', message: `Failed to load prompt: ${error.message}` });
                }
            } finally {
                setIsLoading(false);
            }
        };

        loadPrompt();
    }, [selectedEngineVersion, ensureMeetingAiDefaults]);

    // Load history when engine version or prompt version changes
    useEffect(() => {
        const loadHistory = async () => {
            if (!getJwtToken()) return;

            setIsLoadingHistory(true);
            setHistoryError(null);

            try {
                const data = await promptService.getPromptHistory(selectedEngineVersion);
                // Normalize history items to camelCase
                const normalizedHistory = (data.history || []).map(item => ({
                    version: item.version || item.Version,
                    description: item.description || item.Description,
                    changeNotes: item.changeNotes || item.ChangeNotes,
                    updatedAt: item.updatedAt || item.UpdatedAt,
                    sizeInBytes: item.sizeInBytes || item.SizeInBytes
                }));
                setHistoryItems(normalizedHistory);
            } catch (error) {
                console.error('Failed to load prompt history:', error);
                setHistoryError(error.message);
            } finally {
                setIsLoadingHistory(false);
            }
        };

        loadHistory();
    }, [promptData.version, selectedEngineVersion]); // Reload history when version or engine version changes

    // Clear status after 5 seconds
    useEffect(() => {
        if (status.message) {
            const timer = setTimeout(() => setStatus({ type: '', message: '' }), 5000);
            return () => clearTimeout(timer);
        }
    }, [status]);

    const handleViewHistory = async (version) => {
        try {
            setIsLoadingHistory(true);
            const data = await promptService.getPromptHistoryVersion(version, selectedEngineVersion);
            // Normalize version data
            const normalizedVersion = {
                version: data.version || data.Version,
                systemPromptTemplate: data.systemPromptTemplate || data.SystemPromptTemplate,
                description: data.description || data.Description,
                changeNotes: data.changeNotes || data.ChangeNotes,
                updatedAt: data.updatedAt || data.UpdatedAt,
                sizeInBytes: data.sizeInBytes || data.SizeInBytes
            };
            setSelectedHistoryVersion(normalizedVersion);
            setShowHistoryModal(true);
        } catch (error) {
            console.error('Failed to load version:', error);
            setStatus({ type: 'error', message: `Failed to load version ${version}: ${error.message}` });
        } finally {
            setIsLoadingHistory(false);
        }
    };

    const handleRestoreVersion = async (version) => {
        if (!window.confirm(`Restore version ${version}? This will create a new version with the old content.`)) {
            return;
        }

        try {
            setIsSaving(true);
            // Use the already loaded selectedHistoryVersion if available, otherwise fetch it
            let versionData = selectedHistoryVersion && selectedHistoryVersion.version === version
                ? selectedHistoryVersion
                : await promptService.getPromptHistoryVersion(version, selectedEngineVersion);

            // Normalize in case we fetched it fresh
            const systemPromptTemplate = versionData.systemPromptTemplate || versionData.SystemPromptTemplate || '';
            const description = versionData.description || versionData.Description || '';

            const updateRequest = {
                systemPromptTemplate: systemPromptTemplate,
                description: description,
                changeNotes: `Restored from version ${version}`
            };

            const response = await promptService.updatePrompt(updateRequest, selectedEngineVersion);

            setPromptData(prev => ({
                ...prev,
                version: response.version || response.Version,
                updatedAt: response.updatedAt || response.UpdatedAt,
                systemPromptTemplate: response.systemPromptTemplate || response.SystemPromptTemplate,
                description: response.description || response.Description,
                changeNotes: ''
            }));

            setShowHistoryModal(false);
            setStatus({ type: 'success', message: `Version ${version} restored successfully as v${response.version}` });

            // Reload history
            const historyData = await promptService.getPromptHistory(selectedEngineVersion);
            setHistoryItems(historyData.history || []);
        } catch (error) {
            console.error('Failed to restore version:', error);
            setStatus({ type: 'error', message: `Failed to restore version: ${error.message}` });
        } finally {
            setIsSaving(false);
        }
    };

    const handleSave = async () => {
        if (!promptData.systemPromptTemplate.trim()) {
            setStatus({ type: 'error', message: 'System prompt cannot be empty' });
            return;
        }

        if (!promptData.changeNotes.trim()) {
            setStatus({ type: 'error', message: 'Please provide change notes for this update' });
            return;
        }

        setIsSaving(true);
        try {
            const updateRequest = {
                systemPromptTemplate: promptData.systemPromptTemplate,
                description: promptData.description,
                changeNotes: promptData.changeNotes
            };

            const response = await promptService.updatePrompt(updateRequest, selectedEngineVersion);

            setPromptData(prev => ({
                ...prev,
                version: response.version || response.Version,
                updatedAt: response.updatedAt || response.UpdatedAt,
                changeNotes: '' // Clear notes after successful save
            }));

            setStatus({ type: 'success', message: `Prompt updated successfully for engine ${selectedEngineVersion}` });
        } catch (error) {
            console.error('Failed to update prompt:', error);
            setStatus({ type: 'error', message: `Failed to update prompt: ${error.message}` });
        } finally {
            setIsSaving(false);
        }
    };

    const handleCopyToEngineVersion = async () => {
        if (!copyTargetVersion || copyTargetVersion === selectedEngineVersion) {
            setStatus({ type: 'error', message: 'Please select a different target engine version' });
            return;
        }

        setIsCopying(true);
        try {
            await promptService.copyPromptToEngineVersion(selectedEngineVersion, copyTargetVersion);
            setShowCopyModal(false);
            setStatus({ type: 'success', message: `Prompt copied from ${selectedEngineVersion} to ${copyTargetVersion} successfully` });
        } catch (error) {
            console.error('Failed to copy prompt:', error);
            setStatus({ type: 'error', message: `Failed to copy prompt: ${error.message}` });
        } finally {
            setIsCopying(false);
        }
    };

    const handleFamilyChange = (family) => {
        if (family === promptFamily) return;
        setPromptFamily(family);
        // Reset to the new family's first version; the load effect fetches its prompt.
        setSelectedEngineVersion(PROMPT_FAMILIES[family].versions[0].value);
        setStatus({ type: '', message: '' });
    };

    const handleResetToDefault = async () => {
        const defaults = await ensureMeetingAiDefaults();
        const def = defaults[selectedEngineVersion] || '';
        if (!def) {
            setStatus({ type: 'error', message: 'Default prompt is not available right now. Please try again in a moment.' });
            return;
        }
        setPromptData(prev => ({ ...prev, systemPromptTemplate: def }));
        setStatus({ type: 'success', message: 'Editor reset to the built-in default. Add change notes and Save to apply.' });
    };

    const activeVersions = PROMPT_FAMILIES[promptFamily].versions;

    if (!getJwtToken()) {
        return (
            <div className="prompt-settings-container">
                <div className="loading-state">
                    <Loader2 className="animate-spin" size={48} />
                    <p>Authenticating...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="prompt-settings-container">
            <div className="prompt-header">
                <div className="prompt-header-title">
                    <Button variant="ghost" onClick={() => navigate('/admin-settings')} title="Back to Admin Settings">
                        <ArrowLeft size={24} />
                    </Button>
                    <h1>{PROMPT_FAMILIES[promptFamily].title}</h1>
                </div>
                <div className="prompt-header-actions">
                    {/* Prompt / Engine Version Selector */}
                    <div className="prompt-header-version">
                        <label className="text-sm text-secondary" htmlFor="prompt-engine-version">{isMeetingAi ? 'Prompt:' : 'Engine Version:'}</label>
                        <select
                            id="prompt-engine-version"
                            className="ui-select"
                            value={selectedEngineVersion}
                            onChange={(e) => setSelectedEngineVersion(e.target.value)}
                            disabled={isLoading || isSaving}
                        >
                            {activeVersions.map(v => (
                                <option key={v.value} value={v.value}>{v.label}</option>
                            ))}
                        </select>
                    </div>
                    {isMeetingAi && (
                        <Button
                            variant="secondary"
                            onClick={handleResetToDefault}
                            disabled={isLoading || isSaving}
                            title="Reset the editor to the built-in default instruction"
                        >
                            <RotateCcw size={18} />
                            Reset to Default
                        </Button>
                    )}
                    <Button
                        variant="secondary"
                        onClick={() => {
                            const others = activeVersions.filter(v => v.value !== selectedEngineVersion);
                            setCopyTargetVersion(others[0]?.value || '');
                            setShowCopyModal(true);
                        }}
                        disabled={isLoading || isSaving || !promptData.systemPromptTemplate}
                        title="Copy prompt to another version"
                    >
                        <Copy size={18} />
                        Copy To...
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={isLoading || isSaving}
                        className={isSaving ? 'opacity-70' : ''}
                    >
                        {isSaving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                        Save Changes
                    </Button>
                </div>
            </div>

            {/* Prompt family tabs */}
            <div className="prompt-family-tabs" role="tablist" aria-label="Prompt family">
                {Object.entries(PROMPT_FAMILIES).map(([key, fam]) => (
                    <button
                        key={key}
                        role="tab"
                        aria-selected={promptFamily === key}
                        className={`prompt-family-tab ${promptFamily === key ? 'active' : ''}`}
                        onClick={() => handleFamilyChange(key)}
                        disabled={isLoading || isSaving}
                    >
                        {fam.label}
                    </button>
                ))}
            </div>

            {status.message && (
                <div className={`status-message ${status.type} mb-4`}>
                    {status.type === 'error' ? <AlertCircle size={20} /> : <CheckCircle size={20} />}
                    {status.message}
                </div>
            )}

            {isLoading ? (
                <div className="loading-state">
                    <Loader2 className="animate-spin" size={48} />
                    <p>Loading prompt...</p>
                </div>
            ) : (
                <div className="prompt-content">
                    <div className="prompt-editor-section">
                        <div className="form-group">
                            <label className="ui-input-label" htmlFor="prompt-system-template">
                                {isMeetingAi ? 'Instruction Template' : 'System Prompt Template'}
                            </label>
                            <p className="text-sm text-secondary mb-2">
                                {isMeetingAi
                                    ? (MEETING_AI_HELP[selectedEngineVersion] || 'Meeting AI instruction prompt.')
                                    : 'This is the core instruction set for the translation engine.'}
                            </p>
                            <textarea
                                id="prompt-system-template"
                                className="prompt-textarea"
                                value={promptData.systemPromptTemplate}
                                onChange={(e) => setPromptData({ ...promptData, systemPromptTemplate: e.target.value })}
                                placeholder={isMeetingAi ? 'Enter instruction template…' : 'Enter system prompt...'}
                                spellCheck="false"
                            />
                        </div>
                    </div>

                    <div className="prompt-metadata-section">
                        <div className="metadata-card">
                            <h3 className="mb-4 text-lg font-semibold flex items-center gap-2">
                                <History size={18} /> Version Info
                            </h3>

                            <div className="metadata-item">
                                <label>Current Version</label>
                                <span className="version-badge">v{promptData.version}</span>
                            </div>

                            <div className="metadata-item">
                                <label>Last Updated</label>
                                <span>
                                    {promptData.updatedAt
                                        ? new Date(promptData.updatedAt).toLocaleString()
                                        : 'Never'}
                                </span>
                            </div>

                            <hr className="my-4 border-gray-700" />

                            <div className="form-group mb-4">
                                <Input
                                    label="Description"
                                    value={promptData.description}
                                    onChange={(e) => setPromptData({ ...promptData, description: e.target.value })}
                                    placeholder="e.g. Optimized for medical terms"
                                />
                            </div>

                            <div className="form-group">
                                <label className="ui-input-label" htmlFor="prompt-change-notes">Change Notes (Required)</label>
                                <textarea
                                    id="prompt-change-notes"
                                    className="ui-input w-full p-2 min-h-[100px]"
                                    value={promptData.changeNotes}
                                    onChange={(e) => setPromptData({ ...promptData, changeNotes: e.target.value })}
                                    placeholder="Describe what you changed and why..."
                                />
                            </div>
                        </div>

                        {/* VERSION HISTORY SECTION */}
                        <div className="metadata-card mt-4">
                            <h3 className="mb-4 text-lg font-semibold flex items-center gap-2">
                                <History size={18} /> Version History
                                {historyItems.length > 0 && (
                                    <span className="text-sm text-secondary font-normal">
                                        ({historyItems.length} version{historyItems.length !== 1 ? 's' : ''})
                                    </span>
                                )}
                            </h3>

                            {isLoadingHistory ? (
                                <div className="text-center py-4">
                                    <Loader2 className="animate-spin inline-block" size={24} />
                                    <p className="text-sm text-secondary mt-2">Loading version history...</p>
                                </div>
                            ) : historyError ? (
                                <div className="text-secondary text-sm py-4">
                                    <AlertCircle size={16} className="inline mr-2" />
                                    Failed to load history: {historyError}
                                </div>
                            ) : historyItems.length === 0 ? (
                                <p className="text-secondary text-sm">
                                    No version history available yet. Update the prompt to create version history.
                                </p>
                            ) : (
                                <div className="history-list max-h-[400px] overflow-y-auto border border-gray-700 rounded">
                                    {historyItems.map((item) => (
                                        <div
                                            key={item.version}
                                            className="history-item border-b border-gray-700 last:border-b-0 p-3 hover:bg-gray-800 cursor-pointer transition-colors"
                                            onClick={() => handleViewHistory(item.version)}
                                        >
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="version-badge">v{item.version}</span>
                                                        {item.version === promptData.version && (
                                                            <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded">
                                                                Current
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-sm mt-1" style={{ color: 'var(--text-primary)' }}>
                                                        {item.description || 'No description'}
                                                    </p>
                                                    {item.changeNotes && (
                                                        <p className="text-xs text-secondary mt-1 italic">
                                                            "{item.changeNotes}"
                                                        </p>
                                                    )}
                                                    <p className="text-xs text-secondary mt-1">
                                                        {(item.sizeInBytes / 1024).toFixed(2)} KB
                                                    </p>
                                                </div>
                                                <div className="text-xs text-secondary text-right ml-4">
                                                    <div>{new Date(item.updatedAt).toLocaleDateString()}</div>
                                                    <div className="text-gray-500">
                                                        {new Date(item.updatedAt).toLocaleTimeString()}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* HISTORY DETAIL MODAL */}
            {showHistoryModal && selectedHistoryVersion && (
                <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
                    <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 className="text-xl font-semibold flex items-center gap-2">
                                <History size={20} />
                                Prompt Version {selectedHistoryVersion.version}
                            </h2>
                            <button
                                className="modal-close"
                                onClick={() => setShowHistoryModal(false)}
                                title="Close"
                            >
                                ×
                            </button>
                        </div>

                        <div className="modal-body">
                            {/* Version Metadata */}
                            <div className="mb-4 p-3 bg-gray-800 rounded border border-gray-700">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <label className="text-secondary">Version</label>
                                        <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>v{selectedHistoryVersion.version}</p>
                                    </div>
                                    <div>
                                        <label className="text-secondary">Updated</label>
                                        <p style={{ color: 'var(--text-primary)' }}>
                                            {new Date(selectedHistoryVersion.updatedAt).toLocaleString()}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-secondary">Description</label>
                                        <p style={{ color: 'var(--text-primary)' }}>
                                            {selectedHistoryVersion.description || 'No description'}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-secondary">Size</label>
                                        <p style={{ color: 'var(--text-primary)' }}>
                                            {(selectedHistoryVersion.sizeInBytes / 1024).toFixed(2)} KB
                                        </p>
                                    </div>
                                </div>

                                {selectedHistoryVersion.changeNotes && (
                                    <div className="mt-3 pt-3 border-t border-gray-700">
                                        <label className="text-secondary">Change Notes</label>
                                        <p className="mt-1 italic" style={{ color: 'var(--text-primary)' }}>
                                            "{selectedHistoryVersion.changeNotes}"
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Template Content (Read-Only) */}
                            <div className="mb-4">
                                <label className="ui-input-label" htmlFor="prompt-history-template">System Prompt Template</label>
                                <textarea
                                    id="prompt-history-template"
                                    className="prompt-textarea bg-gray-800"
                                    value={selectedHistoryVersion.systemPromptTemplate}
                                    readOnly
                                    rows={15}
                                />
                            </div>
                        </div>

                        <div className="modal-footer">
                            <Button variant="secondary" onClick={() => setShowHistoryModal(false)}>
                                Close
                            </Button>
                            <Button
                                onClick={() => handleRestoreVersion(selectedHistoryVersion.version)}
                                className="ml-2"
                            >
                                Restore This Version
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* COPY TO ENGINE VERSION MODAL */}
            {showCopyModal && (
                <div className="modal-overlay" onClick={() => setShowCopyModal(false)}>
                    <div className="edit-minutes-modal" onClick={e => e.stopPropagation()}>
                        <h3>Copy Prompt to Another Engine</h3>
                        <p>Copy the current prompt from <strong>{selectedEngineVersion}</strong> to another engine version.</p>
                        <div className="modal-input-group mt-4">
                            <label className="text-sm text-secondary mb-2 block" htmlFor="prompt-copy-target-version">Target Engine Version:</label>
                            <select
                                id="prompt-copy-target-version"
                                className="ui-select w-full"
                                value={copyTargetVersion}
                                onChange={(e) => setCopyTargetVersion(e.target.value)}
                            >
                                {activeVersions
                                    .filter(v => v.value !== selectedEngineVersion)
                                    .map(v => (
                                        <option key={v.value} value={v.value}>{v.label}</option>
                                    ))}
                            </select>
                        </div>
                        <div className="modal-actions mt-4">
                            <Button variant="ghost" onClick={() => setShowCopyModal(false)}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleCopyToEngineVersion}
                                disabled={isCopying}
                            >
                                {isCopying ? <Loader2 className="animate-spin" size={16} /> : <Copy size={16} />}
                                Copy Prompt
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PromptSettings;
